import { MenuItemVariantDto, MenuSubSectionDto, PaymentMethodDto } from '../../generated-api';
import { SettledDigitalBillDto, UnsettledDigitalBillDto } from '../types/digitalBill';
import { getMinutesSince } from './date';

export type SortKey = 'OrderTime' | 'TableNumber' | 'Price';

export const sortDigitalBillsByTableNumber = (
  digitalBills: SettledDigitalBillDto[] | UnsettledDigitalBillDto[] | undefined,
  order: 'ASC' | 'DESC'
) => {
  if (digitalBills === undefined) return [];
  return [...digitalBills].sort((a, b) => {
    if (order === 'ASC') {
      return a.primaryReference.localeCompare(b.primaryReference, undefined, { numeric: true, sensitivity: 'base' });
    }

    return b.primaryReference.localeCompare(a.primaryReference, undefined, { numeric: true, sensitivity: 'base' });
  });
};

export const sortDigitalBillsMinutesSinceLastOrder = (
  digitalBills: SettledDigitalBillDto[] | UnsettledDigitalBillDto[] | undefined,
  order: 'ASC' | 'DESC'
) => {
  if (digitalBills === undefined) return [];
  return [...digitalBills].sort((a, b) => {
    const aMinutesSinceLastOrder = getMinutesSince(a.updated);
    const bMinutesSinceLastOrder = getMinutesSince(b.updated);

    if (order === 'ASC') {
      return aMinutesSinceLastOrder - bMinutesSinceLastOrder;
    }

    return bMinutesSinceLastOrder - aMinutesSinceLastOrder;
  });
};

export const sortDigitalBillsByPrice = (
  digitalBills: SettledDigitalBillDto[] | UnsettledDigitalBillDto[] | undefined,
  order: 'ASC' | 'DESC'
) => {
  if (digitalBills === undefined) return [];
  return [...digitalBills].sort((a, b) => {
    if (order === 'ASC') {
      return a.sum - b.sum;
    }

    return b.sum - a.sum;
  });
};

export const sortDigitalBills = (
  digitalBills: SettledDigitalBillDto[] | UnsettledDigitalBillDto[] | undefined,
  order: 'ASC' | 'DESC',
  sortKey: SortKey
) => {
  if (sortKey === 'OrderTime') return sortDigitalBillsMinutesSinceLastOrder(digitalBills, order);
  if (sortKey === 'Price') return sortDigitalBillsByPrice(digitalBills, order);
  return sortDigitalBillsByTableNumber(digitalBills, order);
};

export const sortUnintegratedPaymentMethods = (paymentMethod1: PaymentMethodDto, paymentMethod2: PaymentMethodDto) => {
  if (paymentMethod1.label && paymentMethod2.label) {
    return paymentMethod1.label.toLowerCase().localeCompare(paymentMethod2.label.toLowerCase());
  }
  return paymentMethod1.paymentMethod.toLowerCase().localeCompare(paymentMethod2.paymentMethod.toLowerCase());
};
type SortAllProductsProps = {
  parentSectionItems?: MenuItemVariantDto[];
  subsections: MenuSubSectionDto[];
};

export const sortAllProducts = ({ parentSectionItems, subsections }: SortAllProductsProps): MenuItemVariantDto[] => {
  const parentItems = parentSectionItems ?? [];

  const maxLength = Math.max(parentItems.length, ...subsections.map((s) => s.items.length));

  const result: MenuItemVariantDto[] = [];

  for (let i = 0; i < maxLength; i += 1) {
    const parent = parentItems[i];
    if (parent) result.push(parent);

    for (const subsection of subsections) {
      const item = subsection.items[i];
      if (item) result.push(item);
    }
  }

  return result;
};
